import { Box, Chip } from "@mui/material"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import { TRANSFER_STATUS_TO_LABEL } from "../../constants"
import { isTransferPartiallyFailed } from "../../utils/transfer"

import { TransferSyncFailedIcon } from "./TransferSyncFailedIcon"

import { type Transfer, TransferAs400SyncStatus, TransferStatus } from "@/graphql/codegen/graphql"

const STATUS_TO_CHIP_CLASS: Record<TransferStatus, string> = {
  [TransferStatus.Draft]: "bg-gray-300",
  [TransferStatus.PendingPickAndPutaway]: "bg-tertiary-yellow-300",
  [TransferStatus.PendingPick]: "bg-tertiary-yellow-300",
  [TransferStatus.PendingShip]: "bg-tertiary-orange-300",
  [TransferStatus.InTransit]: "bg-tertiary-purple-300",
  [TransferStatus.Received]: "bg-tertiary-green-100",
  [TransferStatus.Completed]: "bg-tertiary-green-300",
  [TransferStatus.Cancelled]: "bg-secondary-200",
} as const

// AK-5563 AC1, AC8: pending states share one label; failed states are named per failed action.
const AS400_SYNC_STATUS_TO_LABEL: Record<TransferAs400SyncStatus, string> = {
  [TransferAs400SyncStatus.WorkOrderUpserting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderConfirming]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderDeleting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderUpsertFailed]: "Failed Creation",
  [TransferAs400SyncStatus.WorkOrderConfirmationFailed]: "Failed Completion",
  [TransferAs400SyncStatus.WorkOrderDeletionFailed]: "Failed Cancellation",
  [TransferAs400SyncStatus.AdjustmentUpserting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.AdjustmentUpsertFailed]: "Failed Creation",
  [TransferAs400SyncStatus.AdjustmentDeleting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.AdjustmentDeleteFailed]: "Failed Cancellation",
  [TransferAs400SyncStatus.ConfirmPickSyncing]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.ConfirmPickFailed]: "Failed Pick Confirmation",
} as const

const AS400_SYNC_FAILURE_STATUSES = new Set<TransferAs400SyncStatus>([
  TransferAs400SyncStatus.AdjustmentUpsertFailed,
  TransferAs400SyncStatus.AdjustmentDeleteFailed,
  TransferAs400SyncStatus.ConfirmPickFailed,
  TransferAs400SyncStatus.WorkOrderUpsertFailed,
  TransferAs400SyncStatus.WorkOrderConfirmationFailed,
  TransferAs400SyncStatus.WorkOrderDeletionFailed,
])

interface TransferStatusWithSyncProps {
  transfer: Transfer
}

export const TransferStatusWithSync: FC<TransferStatusWithSyncProps> = ({ transfer }) => {
  const { status, as400SyncStatus } = transfer

  return (
    <Box className='flex flex-col items-start gap-2'>
      <Chip
        className={twMerge("h-6 rounded text-xs", STATUS_TO_CHIP_CLASS[status])}
        label={TRANSFER_STATUS_TO_LABEL[status]}
        size='small'
      />
      {as400SyncStatus && (
        <Box className='flex items-center gap-2'>
          <Chip
            className='h-6 rounded bg-secondary-200 text-xs'
            label={
              isTransferPartiallyFailed(transfer) ? "Partially Failed" : AS400_SYNC_STATUS_TO_LABEL[as400SyncStatus]
            }
            size='small'
          />
          {AS400_SYNC_FAILURE_STATUSES.has(as400SyncStatus) && <TransferSyncFailedIcon />}
        </Box>
      )}
    </Box>
  )
}
